import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Search, Trash2, Edit2, X, FileDown, Upload, RefreshCw, Mail, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["SALE", "REFUND", "EXCHANGE", "HIGH_RISK", "COD_RETURN", "CANCELLED"];
const STATUS_META = {
  SALE:       { label: "Sale",       bg: "bg-[#1A4DFF]", text: "text-white", row: "bg-white",       border: "border-[#1A4DFF]" },
  REFUND:     { label: "Refund",     bg: "bg-[#FF3333]", text: "text-white", row: "bg-[#FFE0E0]",   border: "border-[#FF3333]" },
  EXCHANGE:   { label: "Exchange",   bg: "bg-[#FFB800]", text: "text-black", row: "bg-[#FFF6D6]",   border: "border-[#FFB800]" },
  HIGH_RISK:  { label: "High Risk",  bg: "bg-[#6B0F0F]", text: "text-white", row: "bg-[#FFCCCC]",   border: "border-[#6B0F0F]" },
  COD_RETURN: { label: "COD Return", bg: "bg-[#FF7A00]", text: "text-white", row: "bg-[#FFE0BB]",   border: "border-[#FF7A00]" },
  CANCELLED:  { label: "Cancelled",  bg: "bg-[#6B7280]", text: "text-white", row: "bg-[#F1F2F4]",   border: "border-[#6B7280]" },
};
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};

function defaultYear() {
  const ist = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return ist.getFullYear();
}

export default function DailySales() {
  const [year, setYear] = useState(defaultYear());
  const [monthIdx, setMonthIdx] = useState(() => {
    const ist = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    return ist.getMonth(); // 0..11
  });
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | SALE | REFUND | EXCHANGE
  const [paymentFilter, setPaymentFilter] = useState("ALL"); // ALL | PAID | COD
  const [stateFilter, setStateFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(""); // YYYY-MM-DD, empty = no lower bound
  const [dateTo, setDateTo] = useState(""); // empty = no upper bound
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_status: {}, by_payment: {} });
  const [monthMeta, setMonthMeta] = useState([]); // list from /sales/months
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const monthKey = `${year}-${String(monthIdx + 1).padStart(2, "0")}`;

  async function load() {
    try {
      const params = { month: monthKey };
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (search) params.search = search;
      const [a, b, c] = await Promise.all([
        api.get("/sales", { params }),
        api.get("/sales/stats", { params: { month: monthKey } }),
        api.get("/sales/months"),
      ]);
      setItems(a.data);
      setStats(b.data);
      setMonthMeta(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }
  // eslint-disable-next-line
  useEffect(() => { load(); }, [monthKey, statusFilter, search]);

  // Years derived: fixed sliding window around current year (covers historical + future)
  const years = useMemo(() => {
    const cy = defaultYear();
    const arr = [];
    for (let y = cy - 3; y <= cy + 1; y += 1) arr.push(y);
    return arr;
  }, []);

  // Apply local payment + state filters (server already filtered status+search+month)
  const visible = useMemo(() => {
    return items.filter((s) => {
      if (paymentFilter !== "ALL" && (s.payment_mode || "").toUpperCase() !== paymentFilter) return false;
      if (stateFilter && !((s.state || "").toLowerCase().includes(stateFilter.toLowerCase()))) return false;
      const sd = s.date || "";
      if (dateFrom && sd < dateFrom) return false;
      if (dateTo && sd > dateTo) return false;
      return true;
    });
  }, [items, paymentFilter, stateFilter, dateFrom, dateTo]);

  function onAdd() { setEditing(null); setOpen(true); }
  function onEdit(s) { setEditing(s); setOpen(true); }
  async function onDelete(s) {
    if (s && Array.isArray(s.rows)) {
      const count = s.rows.length;
      const label = s.order_no || "(no order #)";
      if (!window.confirm(`Delete entire entry ${label} (${count} line${count > 1 ? "s" : ""})?`)) return;
      try {
        await Promise.all(s.rows.map((r) => api.delete(`/sales/${r.id}`)));
        toast.success(`Deleted ${count} line${count > 1 ? "s" : ""}`);
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      return;
    }
    if (!window.confirm(`Delete this line?`)) return;
    try { await api.delete(`/sales/${s.id}`); toast.success("Line deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (k) => setExpanded((m) => ({ ...m, [k]: !m[k] }));
  const groupedSales = useMemo(() => {
    const map = new Map();
    for (const s of visible) {
      const key = s.group_id || `${s.order_no || s.id}::${s.date}::${s.status}::${s.payment_mode}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          rows: [],
          order_no: s.order_no,
          date: s.date,
          customer_name: s.customer_name,
          customer_phone: s.customer_phone,
          payment_mode: s.payment_mode,
          state: s.state,
          status: s.status,
        });
      }
      map.get(key).rows.push(s);
    }
    return Array.from(map.values()).map((g) => ({
      ...g,
      totalQty: g.rows.reduce((a, r) => a + (Number(r.quantity) || 0), 0),
      totalAmount: g.rows.reduce((a, r) => a + (Number(r.price) || 0) * (Number(r.quantity) || 0), 0),
      totalRefund: g.rows.reduce((a, r) => a + (Number(r.refund_amount) || 0), 0),
    }));
  }, [visible]);

  async function emailReport() {
    setEmailing(true);
    try {
      const payload = { kind: "sales", month: monthKey };
      if (statusFilter !== "ALL") payload.status = statusFilter;
      if (paymentFilter !== "ALL") payload.payment_mode = paymentFilter;
      if (dateFrom) payload.date_from = dateFrom;
      if (dateTo) payload.date_to = dateTo;
      const { data } = await api.post("/reports/email", payload);
      toast.success(`Report emailed to ${(data.recipients || []).join(", ") || "recipients"} · ${data.rows} rows`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setEmailing(false); }
  }

  async function downloadExcel() {
    try {
      const params = { month: monthKey };
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (paymentFilter !== "ALL") params.payment_mode = paymentFilter;
      const res = await api.get("/exports/sales.xlsx", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `closet-daily-sales-${monthKey}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Daily sales exported.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function onUploadFile(file, replace = false) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/sales/import?replace=${replace ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${res.data.inserted} rows · ${(res.data.months || []).join(", ") || "no months"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setImporting(false); }
  }

  // Per-month counts from monthMeta map for quick badges
  const monthCounts = useMemo(() => {
    const map = {};
    monthMeta.forEach((m) => { map[m.month] = m; });
    return map;
  }, [monthMeta]);

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="daily-sales-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ LEDGER · DAILY SALES</div>
          <h1 className="section-title text-5xl lg:text-6xl">Daily Sales.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Month-wise sales ledger with refund/exchange tracking. Imported from your Excel sheet and editable here.</p>
        </div>
        <div className="flex flex-col gap-2 self-start">
          <button onClick={onAdd} className="brut-btn px-5 py-3 inline-flex items-center gap-2" data-testid="add-sale-btn">
            <Plus size={16} /> New Entry
          </button>
          <button onClick={downloadExcel} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2" data-testid="download-sales-excel-btn">
            <FileDown size={16} /> Download Excel
          </button>
          <button onClick={emailReport} disabled={emailing} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60" data-testid="email-sales-report-btn">
            <Mail size={16} /> {emailing ? "Sending…" : "Email Report"}
          </button>
          <label className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2 cursor-pointer" data-testid="upload-sales-excel-label">
            <Upload size={16} /> {importing ? "Importing…" : "Import Excel"}
            <input type="file" accept=".xlsx" className="hidden" data-testid="upload-sales-excel-input"
              onChange={(e) => { onUploadFile(e.target.files?.[0]); e.target.value = ""; }} disabled={importing} />
          </label>
        </div>
      </header>

      {/* Year selector */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Year</div>
        <div className="flex gap-2">
          {years.map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`year-tab-${y}`}
              className={`px-3 py-1.5 border-2 border-black font-mono-ds text-xs font-bold tracking-wider transition-all ${
                y === year ? "bg-black text-white shadow-[4px_4px_0_0_#0a0a0a]" : "bg-white text-black hover:bg-zinc-100"
              }`}>{y}</button>
          ))}
        </div>
      </div>

      {/* Month tabs Jan-Dec */}
      <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5 mb-5">
        {MONTH_NAMES.map((m, i) => {
          const mk = `${year}-${String(i + 1).padStart(2, "0")}`;
          const meta = monthCounts[mk];
          const isActive = i === monthIdx;
          return (
            <button key={m} onClick={() => setMonthIdx(i)} data-testid={`month-tab-${m.toLowerCase()}`}
              className={`px-2 py-2 border-2 border-black font-display font-bold text-xs uppercase tracking-wider transition-all relative ${
                isActive ? "bg-black text-white shadow-[4px_4px_0_0_#0a0a0a]" : "bg-white text-black hover:bg-zinc-100"
              }`}>
              {m}
              {meta?.count > 0 && (
                <span className={`ml-1 font-mono-ds text-[9px] ${isActive ? "opacity-90" : "opacity-60"}`}>({meta.count})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Status + Payment + State filters */}
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <FilterTab id="ALL" active={statusFilter} setTab={setStatusFilter} label="All" count={STATUSES.reduce((a, s) => a + (stats.by_status?.[s]?.count || 0), 0)} />
        {STATUSES.map((s) => (
          <FilterTab key={s} id={s} active={statusFilter} setTab={setStatusFilter} label={STATUS_META[s].label} count={stats.by_status?.[s]?.count || 0} status={s} />
        ))}
        <div className="w-2" />
        <PayTab id="ALL" active={paymentFilter} setTab={setPaymentFilter} label="All Pay" />
        <PayTab id="PAID" active={paymentFilter} setTab={setPaymentFilter} label="Prepaid" />
        <PayTab id="COD" active={paymentFilter} setTab={setPaymentFilter} label="COD" />
      </div>

      {/* Search + state + date range */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="sales-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by order #, item, design, SKU…" className="brut-input pl-10" />
        </div>
        <input data-testid="sales-state-filter" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
          placeholder="Filter by state (e.g. Mumbai)" className="brut-input max-w-xs" />
        <div className="brut-card p-2 pl-3 flex items-center gap-2 flex-wrap" data-testid="sales-date-range-filter">
          <span className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mr-1">Date range</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="brut-input font-mono-ds text-xs max-w-[150px] py-1.5" data-testid="sales-date-from" />
          <span className="font-mono-ds text-xs text-zinc-500">→</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="brut-input font-mono-ds text-xs max-w-[150px] py-1.5" data-testid="sales-date-to" />
          {(dateFrom || dateTo) && (
            <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); }} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" title="Clear date range" data-testid="sales-date-clear">
              <X size={12} />
            </button>
          )}
        </div>
        <button onClick={load} className="brut-btn-outline px-4 py-2 inline-flex items-center gap-2" data-testid="reload-sales-btn">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Total Rows" value={visible.length} />
        <Stat label="Units" value={visible.reduce((a, s) => a + (s.quantity || 0), 0)} />
        <Stat label="Sales Amount" value={`₹${visible.reduce((a, s) => a + (Number(s.price) || 0) * (Number(s.quantity) || 0), 0).toLocaleString("en-IN")}`} />
        <Stat
          label="Refund Amount (month)"
          value={`₹${Math.round(((stats.by_status?.REFUND?.refund_amount || 0) + (stats.by_status?.COD_RETURN?.refund_amount || 0) + (stats.by_status?.HIGH_RISK?.refund_amount || 0) + (stats.by_status?.SALE?.refund_amount || 0) + (stats.by_status?.EXCHANGE?.refund_amount || 0))).toLocaleString("en-IN")}`}
        />
      </div>

      {/* Per-size count strip */}
      <div className="brut-card p-4 mb-4" data-testid="size-breakdown-strip">
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">Size breakdown (units) · current view</div>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((sz) => {
            const units = visible.reduce((a, s) => a + ((s.size || "").toUpperCase() === sz ? (s.quantity || 0) : 0), 0);
            const rows = visible.filter((s) => (s.size || "").toUpperCase() === sz).length;
            const active = units > 0;
            return (
              <div key={sz} data-testid={`size-count-${sz}`}
                className={`border-2 border-black px-3 py-2 min-w-[88px] ${active ? "bg-black text-white" : "bg-white text-zinc-400"}`}>
                <div className="font-display font-black text-lg leading-none">{units}</div>
                <div className="font-mono-ds text-[10px] tracking-widest uppercase mt-1">{sz} · {rows}r</div>
              </div>
            );
          })}
          {(() => {
            const otherUnits = visible.reduce((a, s) => a + (!SIZES.includes((s.size || "").toUpperCase()) ? (s.quantity || 0) : 0), 0);
            if (otherUnits === 0) return null;
            return (
              <div data-testid="size-count-other" className="border-2 border-black px-3 py-2 min-w-[88px] bg-zinc-200">
                <div className="font-display font-black text-lg leading-none">{otherUnits}</div>
                <div className="font-mono-ds text-[10px] tracking-widest uppercase mt-1">Other</div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-3">
        <Legend status="SALE" />
        <Legend status="REFUND" />
        <Legend status="EXCHANGE" />
        <Legend status="HIGH_RISK" />
        <Legend status="COD_RETURN" />
      </div>

      {/* Table — grouped by order # / group_id so one order = one row with items summary */}
      <div className="brut-card overflow-x-auto">
        <table className="brut-table min-w-[1360px]">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Date</th><th>Order #</th>
              <th className="min-w-[180px]">Customer</th>
              <th className="min-w-[240px]">Items</th>
              <th>Qty</th>
              <th className="min-w-[110px]">Sizes</th>
              <th className="min-w-[110px]">Colors</th>
              <th>Payment</th><th>State</th><th>Status</th><th>Value</th><th>Refund ₹</th>
              <th className="w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupedSales.map((g) => (
              <SaleGroupRow key={g.key} group={g} onEdit={onEdit} onDelete={onDelete}
                expanded={expanded[g.key]} onToggle={() => toggleExpanded(g.key)} />
            ))}
            {groupedSales.length === 0 && (
              <tr><td colSpan={14} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">No entries for this month · Click &quot;New Entry&quot; or &quot;Import Excel&quot;</td></tr>
            )}
          </tbody>
          {groupedSales.length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black" data-testid="sales-totals-row">
                <td colSpan={5} className="text-right pr-4 font-mono-ds text-[11px] tracking-widest uppercase">Totals →</td>
                <td className="font-mono-ds font-black text-base" data-testid="totals-qty">
                  {groupedSales.reduce((a, g) => a + g.totalQty, 0)}
                </td>
                <td colSpan={5}></td>
                <td className="font-mono-ds font-black text-base" data-testid="totals-price">
                  ₹{groupedSales.reduce((a, g) => a + g.totalAmount, 0).toLocaleString("en-IN")}
                </td>
                <td className="font-mono-ds font-black text-base" data-testid="totals-refund">
                  ₹{groupedSales.reduce((a, g) => a + g.totalRefund, 0).toLocaleString("en-IN")}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {open && <SaleForm sale={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} defaultDate={`${monthKey}-01`} />}
    </div>
  );
}

function FilterTab({ id, active, setTab, label, count, status }) {
  const isActive = active === id;
  const accent = status ? STATUS_META[status] : null;
  return (
    <button onClick={() => setTab(id)} data-testid={`status-tab-${id.toLowerCase()}`}
      className={`px-4 py-2 border-2 border-black font-display font-bold text-xs uppercase tracking-wider transition-all ${
        isActive
          ? (accent ? `${accent.bg} ${accent.text}` : "bg-black text-white") + " shadow-[4px_4px_0_0_#0a0a0a]"
          : "bg-white text-black hover:bg-zinc-100"
      }`}>
      {label} <span className="ml-1 font-mono-ds opacity-75">({count})</span>
    </button>
  );
}

function PayTab({ id, active, setTab, label }) {
  const isActive = active === id;
  return (
    <button onClick={() => setTab(id)} data-testid={`pay-tab-${id.toLowerCase()}`}
      className={`px-3 py-2 border-2 border-black font-mono-ds font-bold text-[11px] uppercase tracking-wider transition-all ${
        isActive ? "bg-zinc-900 text-white shadow-[4px_4px_0_0_#0a0a0a]" : "bg-white text-black hover:bg-zinc-100"
      }`}>{label}</button>
  );
}

function Stat({ label, value }) {
  return (
    <div className="brut-card p-4">
      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function SaleGroupRow({ group, onEdit, onDelete, expanded, onToggle }) {
  const meta = STATUS_META[group.status] || {};
  const sizes = [...new Set(group.rows.map((r) => r.size).filter(Boolean))];
  const colors = [...new Set(group.rows.map((r) => r.color).filter(Boolean))];
  const designs = [...new Set(group.rows.map((r) => r.design_name).filter(Boolean))];
  const items = [...new Set(group.rows.map((r) => r.item_name).filter(Boolean))];
  const summary = designs.length > 0
    ? (designs.length === 1 ? designs[0] : `${designs[0]} +${designs.length - 1} more`)
    : (items[0] || "—");
  return (
    <>
      <tr className={`${meta.row || ""} cursor-pointer`} data-testid={`sale-group-${group.order_no || group.key}`} onClick={onToggle}>
        <td className="text-center">
          <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} className="p-1 hover:bg-black/10" aria-label={expanded ? "Collapse" : "Expand"} data-testid={`sale-group-toggle-${group.order_no || group.key}`}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="font-mono-ds text-xs whitespace-nowrap">{group.date}</td>
        <td className="font-mono-ds font-black">{group.order_no || "—"}</td>
        <td className="max-w-[200px]">
          <div className="font-display font-bold truncate" title={group.customer_name}>{group.customer_name || <span className="text-zinc-400">—</span>}</div>
          {group.customer_phone && <div className="font-mono-ds text-[10px] text-zinc-500 truncate" title={group.customer_phone}>{group.customer_phone}</div>}
        </td>
        <td className="font-display font-bold max-w-[280px] truncate" title={[...items, ...designs].join(", ")}>
          {summary}
          <span className="ml-2 font-mono-ds text-[10px] text-zinc-500 uppercase">{group.rows.length} line{group.rows.length > 1 ? "s" : ""}</span>
        </td>
        <td className="font-mono-ds font-black text-base">{group.totalQty}<span className="opacity-50 text-xs ml-1">u</span></td>
        <td className="max-w-[130px]">
          <div className="flex flex-wrap gap-1">
            {sizes.map((s) => <span key={s} className="brut-badge text-[10px] py-0.5">{s}</span>)}
          </div>
        </td>
        <td className="text-xs max-w-[130px] truncate" title={colors.join(", ")}>{colors.join(", ") || "—"}</td>
        <td><span className={`brut-badge ${group.payment_mode === "COD" ? "warning" : "success"}`}>{group.payment_mode}</span></td>
        <td className="text-xs max-w-[140px] truncate" title={group.state}>{group.state || "—"}</td>
        <td><StatusBadge status={group.status} /></td>
        <td className="font-mono-ds font-black">₹{group.totalAmount.toLocaleString("en-IN")}</td>
        <td className="font-mono-ds text-xs font-bold">{group.totalRefund > 0 ? `₹${group.totalRefund.toLocaleString("en-IN")}` : "—"}</td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1">
            <button onClick={() => onEdit(group.rows[0])} className="p-1.5 border-2 border-black hover:bg-zinc-100" title="Edit first line" data-testid={`edit-sale-${group.order_no || group.key}`}><Edit2 size={12} /></button>
            <button onClick={() => onDelete(group)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" title="Delete all lines" data-testid={`delete-sale-${group.order_no || group.key}`}><Trash2 size={12} /></button>
          </div>
        </td>
      </tr>
      {expanded && group.rows.map((r) => (
        <tr key={r.id} className="bg-[#FDF6EE]/60" data-testid={`sale-line-${r.id}`}>
          <td></td>
          <td colSpan={3} className="font-mono-ds text-[10px] text-zinc-500 uppercase text-right pr-2">└ line</td>
          <td className="font-display font-bold text-sm max-w-[280px] truncate" title={`${r.item_name || ""} · ${r.design_name || ""}`}>
            {(r.item_name || "—")} {r.design_name && <span className="text-zinc-500">· {r.design_name}</span>}
          </td>
          <td className="font-mono-ds font-bold">{r.quantity}</td>
          <td><span className="brut-badge text-[10px]">{r.size || "—"}</span></td>
          <td className="text-xs">{r.color || "—"}</td>
          <td></td>
          <td></td>
          <td></td>
          <td className="font-mono-ds text-xs">{r.price ? `₹${Number(r.price).toLocaleString("en-IN")} × ${r.quantity}` : "—"}</td>
          <td className="font-mono-ds text-xs">{Number(r.refund_amount) > 0 ? `₹${Number(r.refund_amount).toLocaleString("en-IN")}` : "—"}</td>
          <td>
            <div className="flex gap-1">
              <button onClick={() => onEdit(r)} className="p-1.5 border-2 border-black hover:bg-zinc-100" title="Edit this line" data-testid={`edit-sale-line-${r.id}`}><Edit2 size={11} /></button>
              <button onClick={() => onDelete(r)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" title="Delete this line only" data-testid={`delete-sale-line-${r.id}`}><Trash2 size={11} /></button>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
}


function StatusBadge({ status }) {
  const m = STATUS_META[status];
  if (!m) return <span className="brut-badge">{status}</span>;
  return <span className={`brut-badge ${m.bg} ${m.text}`} style={{ borderColor: "#0a0a0a" }}>{m.label}</span>;
}

function Legend({ status }) {
  const m = STATUS_META[status];
  return (
    <div className="inline-flex items-center gap-2 text-xs">
      <span className={`inline-block w-4 h-4 border-2 border-black ${m.row}`} />
      <span className="font-mono-ds text-[10px] tracking-widest uppercase">{m.label}</span>
    </div>
  );
}

function SaleForm({ sale, onClose, onSaved, defaultDate }) {
  const isEdit = !!sale;
  const emptyBlock = () => ({
    item_name: "", design_name: "", sku: "", color: "", price: 0,
    sizes: [], sizeQty: {},
  });
  const [form, setForm] = useState(() => ({
    date: sale?.date || defaultDate || todayIST(),
    order_no: sale?.order_no || "",
    customer_name: sale?.customer_name || "",
    customer_phone: sale?.customer_phone || "",
    payment_mode: sale?.payment_mode || "PAID",
    state: sale?.state || "",
    return_date: sale?.return_date || "",
    exchange_size: sale?.exchange_size || "",
    exchange_date: sale?.exchange_date || "",
    refund_amount: sale?.refund_amount ?? 0,
    status: sale?.status || "SALE",
    notes: sale?.notes || "",
    products: sale
      ? [{
          item_name: sale.item_name || "",
          design_name: sale.design_name || "",
          sku: sale.sku || "",
          color: sale.color || "",
          price: sale.price ?? 0,
          sizes: sale.size ? [sale.size] : [],
          sizeQty: sale.size ? { [sale.size]: sale.quantity ?? 1 } : {},
        }]
      : [emptyBlock()],
  }));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  function setBlock(i, patch) {
    setForm((f) => ({ ...f, products: f.products.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  }
  function addBlock() { setForm((f) => ({ ...f, products: [...f.products, emptyBlock()] })); }
  function removeBlock(i) {
    setForm((f) => ({ ...f, products: f.products.length > 1 ? f.products.filter((_, idx) => idx !== i) : f.products }));
  }
  function toggleSize(i, sz) {
    setForm((f) => ({
      ...f,
      products: f.products.map((p, idx) => {
        if (idx !== i) return p;
        if (isEdit) return { ...p, sizes: [sz], sizeQty: { [sz]: p.sizeQty[sz] || 1 } };
        const has = p.sizes.includes(sz);
        if (has) {
          const sq = { ...p.sizeQty }; delete sq[sz];
          return { ...p, sizes: p.sizes.filter((s) => s !== sz), sizeQty: sq };
        }
        return { ...p, sizes: [...p.sizes, sz], sizeQty: { ...p.sizeQty, [sz]: 1 } };
      }),
    }));
  }
  function setSizeQty(i, sz, val) {
    const n = Math.max(1, Math.floor(Number(val) || 1));
    setForm((f) => ({
      ...f,
      products: f.products.map((p, idx) => idx === i ? { ...p, sizeQty: { ...p.sizeQty, [sz]: n } } : p),
    }));
  }

  const totalRows = form.products.reduce((a, p) => a + p.sizes.length, 0);
  const totalUnits = form.products.reduce((a, p) =>
    a + p.sizes.reduce((s, sz) => s + (Number(p.sizeQty[sz]) || 0), 0), 0);
  const totalAmount = form.products.reduce((a, p) => {
    const units = p.sizes.reduce((s, sz) => s + (Number(p.sizeQty[sz]) || 0), 0);
    return a + units * (Number(p.price) || 0);
  }, 0);
  const refundVal = Number(form.refund_amount) || 0;
  const netAmount = totalAmount - refundVal;

  async function save() {
    const cleanedBlocks = form.products
      .map((p) => ({ ...p, sizes: p.sizes.filter(Boolean) }))
      .filter((p) => p.sizes.length > 0);
    if (cleanedBlocks.length === 0) { toast.error("Pick at least one size on at least one product"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const p = cleanedBlocks[0];
        const sz = p.sizes[0];
        const payload = {
          date: form.date,
          order_no: form.order_no,
          customer_name: form.customer_name,
          customer_phone: form.customer_phone,
          status: form.status,
          payment_mode: form.payment_mode,
          state: form.state,
          return_date: form.return_date,
          exchange_size: form.exchange_size,
          exchange_date: form.exchange_date,
          notes: form.notes,
          refund_amount: Number(form.refund_amount) || 0,
          item_name: p.item_name,
          design_name: p.design_name,
          sku: p.sku,
          color: p.color,
          price: Number(p.price) || 0,
          size: sz,
          quantity: Number(p.sizeQty[sz]) || 1,
        };
        await api.put(`/sales/${sale.id}`, payload);
        toast.success("Entry updated");
      } else {
        const line_items = cleanedBlocks.map((p) => ({
          item_name: p.item_name,
          design_name: p.design_name,
          sku: p.sku,
          color: p.color,
          price: Number(p.price) || 0,
          sizes: p.sizes,
          quantities: p.sizes.map((s) => Number(p.sizeQty[s]) || 1),
        }));
        const payload = {
          date: form.date,
          order_no: form.order_no,
          customer_name: form.customer_name,
          customer_phone: form.customer_phone,
          status: form.status,
          payment_mode: form.payment_mode,
          state: form.state,
          return_date: form.return_date,
          exchange_size: form.exchange_size,
          exchange_date: form.exchange_date,
          notes: form.notes,
          refund_amount: Number(form.refund_amount) || 0,
          line_items,
        };
        const res = await api.post("/sales", payload);
        const count = res.data?.count || 1;
        toast.success(count > 1 ? `Added ${count} sale rows` : "Entry added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  const meta = STATUS_META[form.status];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="sale-form-modal">
      <div className="brut-card w-full max-w-3xl bg-white">
        <div className={`flex items-center justify-between p-5 border-b-2 border-black ${meta.bg} ${meta.text}`}>
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-80">{isEdit ? "EDIT" : "NEW"} · {meta.label}</div>
            <h2 className="font-display font-black text-2xl tracking-tight">{isEdit ? "Edit Sale Entry" : "New Sale Entry"}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/10" data-testid="close-sale-form"><X size={20} /></button>
        </div>

        {/* Shared header */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-b-2 border-black">
          <Field label="Date *">
            <input type="date" className="brut-input font-mono-ds" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="sale-date-input" />
          </Field>
          <Field label="Order #">
            <input className="brut-input font-mono-ds" value={form.order_no} onChange={(e) => set("order_no", e.target.value)} data-testid="sale-order-no-input" placeholder="e.g. 4337" />
          </Field>
          <Field label="Customer Name">
            <input className="brut-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="sale-customer-name-input" placeholder="e.g. Rahul Sharma" />
          </Field>
          <Field label="Customer Phone">
            <input type="tel" className="brut-input font-mono-ds" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="sale-customer-phone-input" placeholder="+91 98765 43210" />
          </Field>
          <Field label="Status">
            <select className="brut-input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="sale-status-select">
              <option value="SALE">Sale</option>
              <option value="REFUND">Refund (red)</option>
              <option value="EXCHANGE">Exchange (yellow)</option>
              <option value="HIGH_RISK">High Risk (dark red)</option>
              <option value="COD_RETURN">COD Return (orange)</option>
              <option value="CANCELLED">Cancelled by Customer</option>
            </select>
          </Field>
          <Field label="Payment">
            <select className="brut-input" value={form.payment_mode} onChange={(e) => set("payment_mode", e.target.value)} data-testid="sale-payment-select">
              <option value="PAID">PAID</option>
              <option value="COD">COD</option>
            </select>
          </Field>
          <Field label="State">
            <input className="brut-input" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="sale-state-input" placeholder="Mumbai MH" />
          </Field>
          <Field label="Return Date">
            <input type="date" className="brut-input font-mono-ds" value={form.return_date} onChange={(e) => set("return_date", e.target.value)} data-testid="sale-return-date-input" />
          </Field>
          <Field label="Exchange Size">
            <select className="brut-input" value={form.exchange_size} onChange={(e) => set("exchange_size", e.target.value)} data-testid="sale-exchange-size-select">
              <option value="">—</option>
              {SIZES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Exchange Date">
            <input type="date" className="brut-input font-mono-ds" value={form.exchange_date} onChange={(e) => set("exchange_date", e.target.value)} data-testid="sale-exchange-date-input" />
          </Field>
          <Field label="Refund Amount (₹)">
            <input type="number" min={0} step="0.01" className="brut-input font-mono-ds" value={form.refund_amount} onChange={(e) => set("refund_amount", e.target.value)} data-testid="sale-refund-amount-input" placeholder="0" />
          </Field>
        </div>

        {/* Product line-item blocks */}
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-600">
              Products · {form.products.length} block{form.products.length > 1 ? "s" : ""}
            </div>
            {!isEdit && (
              <button type="button" onClick={addBlock} className="brut-btn-outline px-3 py-1.5 inline-flex items-center gap-1.5 text-xs" data-testid="add-sale-line-btn">
                <Plus size={12} /> Add Product
              </button>
            )}
          </div>

          {form.products.map((p, i) => (
            <div key={i} className="brut-card p-4 bg-[#FDF6EE]/60" data-testid={`sale-line-block-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-600">Product #{i + 1}</div>
                {!isEdit && form.products.length > 1 && (
                  <button type="button" onClick={() => removeBlock(i)} className="p-1.5 border-2 border-black hover:bg-[#FF3333] hover:text-white" data-testid={`remove-sale-line-${i}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Item Name">
                  <input className="brut-input" value={p.item_name} onChange={(e) => setBlock(i, { item_name: e.target.value })} data-testid={`sale-item-input-${i}`} placeholder="Cotton oversize 240 gsm" />
                </Field>
                <Field label="Design Name">
                  <input className="brut-input" value={p.design_name} onChange={(e) => setBlock(i, { design_name: e.target.value })} data-testid={`sale-design-input-${i}`} placeholder="Pink Floyd Prism" />
                </Field>
                <Field label="Color">
                  <input className="brut-input" value={p.color} onChange={(e) => setBlock(i, { color: e.target.value })} data-testid={`sale-color-input-${i}`} />
                </Field>
                <Field label="SKU">
                  <input className="brut-input font-mono-ds" value={p.sku} onChange={(e) => setBlock(i, { sku: e.target.value })} data-testid={`sale-sku-input-${i}`} />
                </Field>
                <Field label="Price (₹)">
                  <input type="number" className="brut-input font-mono-ds" value={p.price} onChange={(e) => setBlock(i, { price: e.target.value })} data-testid={`sale-price-input-${i}`} />
                </Field>
                <div />
                <div className="md:col-span-3">
                  <Field label={isEdit ? "Size · Qty" : "Sizes · per-size Qty"}>
                    <div className="flex flex-wrap gap-2" data-testid={`sale-sizes-chips-${i}`}>
                      {SIZES.map((s) => {
                        const active = p.sizes.includes(s);
                        return (
                          <div key={s} className={`inline-flex items-stretch border-2 border-black ${active ? "shadow-[3px_3px_0_0_#0a0a0a]" : ""}`}>
                            <button type="button" onClick={() => toggleSize(i, s)} data-testid={`sale-size-chip-${i}-${s}`}
                              className={`px-3 py-1.5 font-mono-ds text-xs font-bold tracking-wider transition-all min-w-[44px] ${
                                active ? "bg-black text-white" : "bg-white text-black hover:bg-zinc-100"
                              }`}>{s}</button>
                            {active && (
                              <input type="number" min={1} value={p.sizeQty[s] ?? 1}
                                onChange={(e) => setSizeQty(i, s, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`sale-size-qty-${i}-${s}`}
                                className="w-14 px-1.5 text-center font-mono-ds font-bold border-l-2 border-black bg-white text-black focus:outline-none" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {p.sizes.length > 0 && (
                      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-600 mt-1.5">
                        → {p.sizes.length} row{p.sizes.length > 1 ? "s" : ""} · {p.sizes.reduce((a, s) => a + (Number(p.sizeQty[s]) || 0), 0)} units · ₹{(p.sizes.reduce((a, s) => a + (Number(p.sizeQty[s]) || 0), 0) * (Number(p.price) || 0)).toLocaleString("en-IN")}
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>
          ))}

          <Field label="Notes (shared for all products in this entry)">
            <textarea rows={2} className="brut-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="sale-notes-input" />
          </Field>
        </div>

        <div className="border-t-2 border-black p-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="bg-black text-white px-4 py-3 flex flex-wrap items-center gap-4 border-2 border-black shadow-[4px_4px_0_0_#0a0a0a]" data-testid="sale-form-totals">
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">Rows · Units</div>
              <div className="font-display font-black text-lg leading-none">{totalRows} · {totalUnits}u</div>
              <div className="font-mono-ds text-[10px] opacity-70 mt-0.5">{form.products.length} product{form.products.length > 1 ? "s" : ""}</div>
            </div>
            <div className="w-px h-8 bg-white/30" />
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">Total Amount</div>
              <div className="font-display font-black text-lg leading-none" data-testid="sale-form-total-amount">₹{totalAmount.toLocaleString("en-IN")}</div>
            </div>
            {refundVal > 0 && (
              <>
                <div className="w-px h-8 bg-white/30" />
                <div>
                  <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70 text-[#FFB3B3]">− Refund</div>
                  <div className="font-display font-black text-lg leading-none">−₹{refundVal.toLocaleString("en-IN")}</div>
                </div>
                <div className="w-px h-8 bg-white/30" />
                <div>
                  <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">Net</div>
                  <div className="font-display font-black text-lg leading-none">₹{netAmount.toLocaleString("en-IN")}</div>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="brut-btn-outline px-5 py-2.5" data-testid="cancel-sale-btn">Cancel</button>
            <button onClick={save} disabled={busy} className="brut-btn px-5 py-2.5 disabled:opacity-60" data-testid="save-sale-btn">
              {busy ? "Saving…" : (isEdit ? "Save Changes" : `Save Entry${form.products.length > 1 ? ` (${form.products.length} products)` : ""}`)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-ds text-[11px] tracking-widest uppercase mb-1.5">{label}</div>
      {children}
    </label>
  );
}
